import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  fetchCollectionSources,
  fetchSource,
  fetchSourceReadme,
  fetchSourceComments,
  createSourceComment,
  fetchSourceStateTransitions,
  fetchSourceHistory,
  fetchSourceReadmeVersions,
  fetchHistoricalSourceMetrics,
  updateSource,
  updateSourcesParams,
  createSource,
  updateSourceReadme,
  runSource,
  updateExtractorOwner,
  migrateLatestRuntimeConfig,
} from '_/api/sources/sources.api';
import { ValidationError } from '_/common/types/errors.types';
import { checkIfNameExists } from '_/utils/validation/nameValidator';
import { checkIfSlugExists } from '_/utils/validation/slugValidator';
import { notification } from 'antd';

export const SOURCES_QUERY_KEY = ['sources'];
export const SOURCE_QUERY_KEY = (sourceId: string): string[] => ['source', sourceId];
export const SOURCE_README_QUERY_KEY = (sourceId: string): string[] => ['source', sourceId, 'readme'];
export const SOURCE_COMMENTS_QUERY_KEY = (sourceId: string): string[] => ['source', sourceId, 'comments'];
export const SOURCE_STATE_TRANSITIONS_QUERY_KEY = (sourceId: string): string[] => ['source', sourceId, 'stateTransitions'];
export const SOURCE_HISTORY_QUERY_KEY = (sourceId: string): string[] => ['source', sourceId, 'history'];
export const SOURCE_README_VERSIONS_QUERY_KEY = (sourceId: string): string[] => ['source', sourceId, 'readmeVersions'];
export const HISTORICAL_METRICS_QUERY_KEY = (sourceId: string, checkId: string): string[] => [
  'source',
  sourceId,
  'historicalMetrics',
  checkId,
];

export const useCollectionSources = (collectionId?: string): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: [...SOURCES_QUERY_KEY, collectionId],
    queryFn: () => fetchCollectionSources(collectionId!),
    enabled: !!collectionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSource = (collectionId?: string, sourceSlug?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: [...SOURCE_QUERY_KEY(sourceSlug || ''), collectionId],
    queryFn: () => fetchSource(collectionId!, sourceSlug!),
    enabled: !!collectionId && !!sourceSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSourceReadme = (sourceId?: string): UseQueryResult<string> => {
  return useQuery<string>({
    queryKey: SOURCE_README_QUERY_KEY(sourceId || ''),
    queryFn: () => fetchSourceReadme(sourceId!),
    enabled: !!sourceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSourceComments = (sourceId?: string): UseQueryResult<any[]> => {
  return useQuery<any[]>({
    queryKey: SOURCE_COMMENTS_QUERY_KEY(sourceId || ''),
    queryFn: () => fetchSourceComments(sourceId!),
    enabled: !!sourceId,
    staleTime: 30 * 1000, // 30 seconds - comments change frequently
  });
};

export const useSourceStateTransitions = (sourceId?: string): UseQueryResult<any[]> => {
  return useQuery<any[]>({
    queryKey: SOURCE_STATE_TRANSITIONS_QUERY_KEY(sourceId || ''),
    queryFn: () => fetchSourceStateTransitions(sourceId!),
    enabled: !!sourceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSourceHistory = (sourceId?: string, cursor?: string): UseQueryResult<{ items: any[]; cursor: string | null }> => {
  return useQuery<{ items: any[]; cursor: string | null }>({
    queryKey: [...SOURCE_HISTORY_QUERY_KEY(sourceId || ''), cursor || 'initial'],
    queryFn: () => fetchSourceHistory(sourceId!, cursor),
    enabled: !!sourceId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useSourceReadmeVersions = (
  sourceId?: string,
  cursor?: string,
): UseQueryResult<{ items: any[]; cursor: string | null }> => {
  return useQuery<{ items: any[]; cursor: string | null }>({
    queryKey: [...SOURCE_README_VERSIONS_QUERY_KEY(sourceId || ''), cursor || 'initial'],
    queryFn: () => fetchSourceReadmeVersions(sourceId!, cursor),
    enabled: !!sourceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useHistoricalSourceMetrics = (sourceId?: string, checkId?: string): UseQueryResult<Record<string, any[]>> => {
  return useQuery<Record<string, any[]>>({
    queryKey: HISTORICAL_METRICS_QUERY_KEY(sourceId || '', checkId || ''),
    queryFn: () => fetchHistoricalSourceMetrics(sourceId!, checkId!),
    enabled: !!sourceId && !!checkId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useUpdateSource = (
  collectionId?: string,
): UseMutationResult<any, Error, { sourceId: string; updates: Record<string, any> }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, updates }: { sourceId: string; updates: Record<string, any> }) => updateSource(sourceId, updates),
    onSuccess: (_, variables) => {
      if (collectionId) {
        void queryClient.invalidateQueries({ queryKey: [...SOURCES_QUERY_KEY, collectionId] });
      }

      // Invalidate all source queries that might contain this source
      // This handles both individual source queries and cached source lists
      void queryClient.invalidateQueries({
        predicate: (query) => {
          // Match any query that starts with ['source']
          const queryKey = query.queryKey as string[];
          if (queryKey[0] === 'source') {
            // Check if the query data contains our sourceId
            const queryData = query.state.data as any;
            if (queryData?.id === variables.sourceId) {
              return true;
            }
            // Also match if the sourceId is in the query key itself
            if (queryKey.includes(variables.sourceId)) {
              return true;
            }
          }
          return false;
        },
      });

      // If readme was updated, invalidate readme and readme versions
      if (variables.updates.sourceReadme !== undefined) {
        void queryClient.invalidateQueries({ queryKey: SOURCE_README_QUERY_KEY(variables.sourceId) });
        void queryClient.invalidateQueries({
          queryKey: SOURCE_README_VERSIONS_QUERY_KEY(variables.sourceId),
        });
      }

      notification.success({
        message: 'Source Updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating source', error);
      notification.error({
        message: error.message || 'Error updating source',
      });
    },
  });
};

export const useUpdateSourcesParams = (
  collectionId?: string,
): UseMutationResult<any, Error, { sources: any[]; params: Record<string, any> }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sources, params }: { sources: any[]; params: Record<string, any> }) => updateSourcesParams(sources, params),
    onSuccess: () => {
      if (collectionId) {
        void queryClient.invalidateQueries({ queryKey: [...SOURCES_QUERY_KEY, collectionId] });
      }
      notification.success({
        message: 'Parameters Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating source parameters', error);
      notification.error({
        message: error.message || 'Error updating parameters',
      });
    },
  });
};

export const useCreateSource = (
  collectionId: string,
  orgSlug: string,
  projSlug: string,
  collSlug: string,
  existingSources: any[],
): UseMutationResult<any, ValidationError, { source: Record<string, any>; sourceReadme: string }> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: ({ source, sourceReadme }: { source: Record<string, any>; sourceReadme: string }) => {
      // Validate name and slug don't already exist
      if (checkIfNameExists(existingSources, source.name)) {
        throw new ValidationError([{ type: 'name', error: `A source with the name "${source.name}" already exists` }]);
      }

      if (checkIfSlugExists(existingSources, source.slug)) {
        throw new ValidationError([{ type: 'slug', error: `A source with the id/slug "${source.slug}" already exists` }]);
      }

      return createSource(collectionId, source, sourceReadme);
    },
    onSuccess: (newSource) => {
      // Optimistically update the cache with the new source
      queryClient.setQueryData([...SOURCES_QUERY_KEY, collectionId], (oldData: any[] | undefined) => {
        if (!oldData) return [newSource];
        return [...oldData, newSource];
      });

      // Also invalidate to ensure server data is fetched
      void queryClient.invalidateQueries({ queryKey: [...SOURCES_QUERY_KEY, collectionId] });

      notification.success({
        message: 'Source Saved',
      });

      void history({
        to: `/orgs/${orgSlug}/projects/${projSlug}/collections/${collSlug}/sources/${newSource.slug}`,
        replace: true,
      });
    },
    onError: (error: any) => {
      console.error('Error creating source', error);

      const errorMessage = error.messages
        ? error.messages.map((m: any) => m.error || m.message).join(', ')
        : error.message || 'Error creating source';

      notification.error({
        message: 'Failed to create source',
        description: errorMessage,
      });

      // Convert error to ValidationError if it's not already
      const validationError =
        error instanceof ValidationError
          ? error
          : error.messages
            ? new ValidationError(error.messages)
            : new ValidationError([{ type: 'general', error: error.message || 'Error creating source' }]);

      // Error will be returned to the component to display field-specific errors
      throw validationError;
    },
  });
};

export const useUpdateSourceReadme = (sourceId: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => updateSourceReadme(sourceId, content),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SOURCE_README_QUERY_KEY(sourceId) });
      void queryClient.invalidateQueries({ queryKey: SOURCE_README_VERSIONS_QUERY_KEY(sourceId) });

      notification.success({
        message: 'Source Updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating source readme', error);
      notification.error({
        message: error.message || 'Error updating source readme',
      });
    },
  });
};

export const useCreateSourceComment = (sourceId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (comment: string) => createSourceComment(sourceId!, comment),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SOURCE_COMMENTS_QUERY_KEY(sourceId!) });

      notification.success({
        message: 'Comment Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error saving comment', error);
      notification.error({
        message: error.message || 'Error saving comment',
      });
    },
  });
};

export const useRunSource = (
  sourceId: string,
  orgSlug: string,
  projectSlug: string,
  collectionSlug: string,
  sourceSlug: string,
): UseMutationResult<any, Error, void> => {
  const history = useNavigate();

  return useMutation({
    mutationFn: () => runSource(sourceId),
    onSuccess: (snapshot) => {
      notification.success({
        message: 'Snapshot created',
      });

      // Navigate to the newly created snapshot
      void history({
        to: `/orgs/${orgSlug}/projects/${projectSlug}/collections/${collectionSlug}/sources/${sourceSlug}/snapshots/${snapshot.id}`,
      });
    },
    onError: (error: any) => {
      console.error('Error running source', error);
      notification.error({
        message: error.message || 'Error running source',
      });
    },
  });
};

export const useUpdateExtractorOwner = (sourceId: string): UseMutationResult<any, Error, { extractorId: string; body: any }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ extractorId, body }: { extractorId: string; body: any }) => updateExtractorOwner(extractorId, body),
    onSuccess: () => {
      // Invalidate source and extractor data
      void queryClient.invalidateQueries({ queryKey: SOURCE_QUERY_KEY(sourceId) });

      notification.success({
        message: 'Extractor updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating extractor owner', error);
      notification.error({
        message: error.message || 'Error updating extractor owner',
      });
    },
  });
};

export const useMigrateLatestRuntimeConfig = (sourceId: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => migrateLatestRuntimeConfig(sourceId),
    onSuccess: () => {
      // Invalidate all source and extractor-related queries to fully refresh the page
      void queryClient.invalidateQueries({ queryKey: ['source'] });
      void queryClient.invalidateQueries({ queryKey: SOURCES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['extractor'] });
      void queryClient.invalidateQueries({ queryKey: ['legacyExtractor'] });

      notification.success({
        message: 'Extractor migrated successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error migrating latest runtime config', error);
      notification.error({
        message: error.message || 'Error migrating latest runtime config',
      });
    },
  });
};
